import React, { useState, useEffect } from "react";
import LayoutAdmin from "../../components/layout/LayoutAdmin";
import TransactionPreviewModal from "../../components/models/TransactionPreviewModal";
import CancelPaymentAuthenticationFailedModal from "../../components/models/CancelPaymentAuthenticationFailedModal";
import apiService from "../../actions/apiService";

const Avatar = ({ imageUrl, firstName, lastName }) => (
  <span className="w-8 h-8 rounded-full bg-slate-200 inline-flex items-center justify-center text-[10px] text-slate-500">
    {imageUrl ? (
      <img
        src={imageUrl}
        alt="avatar"
        className="h-full w-full rounded-full object-cover"
      />
    ) : (
      <span className="h-full w-full rounded-full bg-slate-200 inline-flex items-center justify-center text-[10px] text-slate-500">
        {firstName?.charAt(0) + lastName?.charAt(0)}
      </span>
    )}
  </span>
);

const AdminTransactions = () => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState(null);
  const [transactionsData, setTransactionsData] = useState(null);
  const [page] = useState(1);
  const [limit] = useState(100);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sendingReminder, setSendingReminder] = useState(null); // Track which transaction is sending
  const [sentReminder, setSentReminder] = useState(null); // Track which transaction email was sent
  const [releasingFunds, setReleasingFunds] = useState(null); // Track which transaction is releasing funds
  const [acceptingPayment, setAcceptingPayment] = useState(null); // Track which transaction is accepting payment
  const [reportingMissingPayment, setReportingMissingPayment] = useState(null); // Track "payment not received" action
  const [reportedMissingPayment, setReportedMissingPayment] = useState(null); // Track success state
  const [cancellingPayment, setCancellingPayment] = useState(null); // Track "cancel payment authentication failed" action
  const [cancelledPayment, setCancelledPayment] = useState(null); // Track success state
  const [cancelAuthModalOpen, setCancelAuthModalOpen] = useState(false);
  const [selectedTransactionForCancel, setSelectedTransactionForCancel] =
    useState(null);

  const handleOpenPreview = (transaction) => {
    setSelectedTransactionId(transaction.id);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedTransactionId(null);
  };

  const handleSendReminder = async (transaction) => {
    // Determine recipient based on transaction status
    let recipient = "buyer";

    // If seller hasn't confirmed but buyer has, remind seller
    if (!transaction.seller_confirmed && transaction.buyer_confirmed) {
      recipient = "seller";
    }
    // If buyer hasn't confirmed payment/receipt, remind buyer
    else if (
      !transaction.buyer_confirmed ||
      transaction.status === "pending" ||
      transaction.status === "accepted"
    ) {
      recipient = "buyer";
    }
    // If seller hasn't confirmed delivery, remind seller
    else if (!transaction.seller_confirmed) {
      recipient = "seller";
    }

    setSendingReminder(transaction.id);
    setSentReminder(null);
    try {
      const res = await apiService.post(
        `/admin/transactions/${transaction.id}/send-reminder`,
        { recipient }
      );
      if (res?.success) {
        // Show "Email sent" on button
        setSendingReminder(null);
        setSentReminder(transaction.id);
        // Reset button text after 2 seconds
        setTimeout(() => {
          setSentReminder(null);
        }, 2000);
      } else {
        setSendingReminder(null);
      }
    } catch (err) {
      console.error("Error sending reminder:", err);
      setSendingReminder(null);
    }
  };

  const handleReleaseFunds = async (transaction) => {
    setReleasingFunds(transaction.id);
    try {
      const res = await apiService.post(
        `/admin/transactions/${transaction.id}/release`
      );
      if (res?.success) {
        // Update the transaction in local state
        setTransactionsData((prev) => {
          if (!prev?.transactions) return prev;
          return {
            ...prev,
            transactions: prev.transactions.map((tx) =>
              tx.id === transaction.id
                ? { ...tx, admin_released: true, status: "completed" }
                : tx
            ),
          };
        });
        setReleasingFunds(null);
      } else {
        setReleasingFunds(null);
        setTxError(res?.error || "Failed to release funds");
      }
    } catch (err) {
      console.error("Error releasing funds:", err);
      setReleasingFunds(null);
      setTxError(err?.response?.data?.error || "Failed to release funds");
    }
  };

  const handleAcceptPayment = async (transaction) => {
    setAcceptingPayment(transaction.id);
    try {
      const res = await apiService.post(
        `/admin/transactions/${transaction.id}/accept-payment`
      );
      if (res?.success) {
        // Refresh transactions list
        const refreshRes = await apiService.get("/admin/transactions", {
          page,
          limit,
        });
        if (refreshRes?.success) {
          setTransactionsData(refreshRes.data);
        }
        setAcceptingPayment(null);
      } else {
        setAcceptingPayment(null);
        setTxError(res?.error || "Failed to accept payment");
      }
    } catch (err) {
      console.error("Error accepting payment:", err);
      setAcceptingPayment(null);
      setTxError(err?.response?.data?.error || "Failed to accept payment");
    }
  };

  const handleReportMissingPayment = async (transaction) => {
    setReportingMissingPayment(transaction.id);
    setReportedMissingPayment(null);
    try {
      const res = await apiService.post(
        `/admin/transactions/${transaction.id}/report-missing-payment`,
        {}
      );
      if (res?.success) {
        setReportingMissingPayment(null);
        setReportedMissingPayment(transaction.id);
        setTimeout(() => setReportedMissingPayment(null), 2000);
      } else {
        setReportingMissingPayment(null);
        setTxError(
          res?.error || "Failed to notify buyer about missing payment"
        );
      }
    } catch (err) {
      console.error("Error reporting missing payment:", err);
      setReportingMissingPayment(null);
      setTxError(
        err?.response?.data?.error ||
          "Failed to notify buyer about missing payment"
      );
    }
  };

  const handleOpenCancelAuthModal = (transaction) => {
    setSelectedTransactionForCancel(transaction);
    setCancelAuthModalOpen(true);
  };

  const handleCloseCancelAuthModal = () => {
    setCancelAuthModalOpen(false);
    setSelectedTransactionForCancel(null);
  };

  const handleCancelPaymentAuthenticationFailed = async () => {
    if (!selectedTransactionForCancel) return;

    const transaction = selectedTransactionForCancel;
    setCancellingPayment(transaction.id);
    setTxError(null);
    try {
      const res = await apiService.post(
        `/admin/transactions/${transaction.id}/cancel-payment-authentication-failed`,
        { reason: "Product authentication check failed" }
      );
      if (res?.success) {
        setCancellingPayment(null);
        setCancelledPayment(transaction.id);
        setCancelAuthModalOpen(false);
        setSelectedTransactionForCancel(null);
        // Refresh transactions list
        const refreshRes = await apiService.get("/admin/transactions", {
          page,
          limit,
        });
        if (refreshRes?.success) {
          setTransactionsData(refreshRes.data);
        }
        setTimeout(() => setCancelledPayment(null), 2000);
      } else {
        setCancellingPayment(null);
        setTxError(
          res?.error || "Failed to cancel payment due to authentication failure"
        );
      }
    } catch (err) {
      console.error("Error cancelling payment:", err);
      setCancellingPayment(null);
      setTxError(
        err?.response?.data?.error ||
          "Failed to cancel payment due to authentication failure"
      );
    }
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      setTxLoading(true);
      setTxError(null);
      try {
        const res = await apiService.get("/admin/transactions", {
          page,
          limit,
          search: search || undefined,
        });
        if (res?.success) {
          setTransactionsData(res.data);
          // Optional debug
          // console.log("Admin transactions response:", res.data);
        } else {
          setTxError(res?.error || "Failed to load transactions");
        }
      } catch (_) {
        setTxError("Network error. Please try again.");
      } finally {
        setTxLoading(false);
      }
    };
    fetchTransactions();
  }, [page, limit, search]);

  console.log(transactionsData);

  return (
    <LayoutAdmin>
      {/* Header */}
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative mb-8 z-[10]">
        <div className="flex px-5 items-center justify-between">
          <div>
            <h2 className="text-page-title md:text-page-title-d text-white lg:text-[#1E1E1E] font-rubik">
              Transaction Review
            </h2>
            <p className="text-small-price opacity-50 mt-4 md:text-small-price text-white lg:text-[#1E1E1E] !font-normal">
              Monitor and manage platform transactions
            </p>
          </div>
        </div>
      </div>

      {/* Search transactions */}
      <div className="px-5 lg:px-0 lg:ps-5 relative">
        <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5 mb-5">
          <h4 className="font-Rubik text-[24px] font-[500] text-esdarkGray mb-2">
            Search transactions
          </h4>
          <p className="text-[16px] opacity-50 font-[400] text-[#000025] mb-8">
            Search transactions by item title or ID.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <input
              type="text"
              placeholder="Search transactions..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  setSearch(searchInput.trim());
                }
              }}
              className="flex-1 border border-[#00000026] rounded-[8px] px-3 py-2 text-[14px] placeholder:text-[#1E1E1E] placeholder:opacity-50 placeholder:font-[400] outline-none"
            />
            <button
              onClick={() => {
                setSearch(searchInput.trim());
              }}
              className="inline-flex justify-center items-center gap-2 bg-esblue text-white text-[14px] min-w-full sm:min-w-[174px] px-4 py-2 rounded-[8px]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              Search
            </button>
          </div>
          {txError && (
            <div className="mt-4 text-sm text-red-600">{txError}</div>
          )}
        </div>
      </div>

      {/* Table (desktop) */}
      <div className="px-5 lg:px-0 lg:ps-5 relative">
        <div className="w-full overflow-x-auto bg-white border border-[#DCEDFF] rounded-[12px]">
          <table className="w-[97%] ms-5 mr-5 mx-auto mt-5 mb-2 min-w-[900px]">
            <thead className="bg-[#1E1E1E0D] text-subsection-title-d text-[#1E1E1E]">
              <tr>
                <th className="text-left min-w-[250px] py-3 px-5 rounded-tl-[12px]">
                  Reference ID
                </th>
                <th className="text-left min-w-[280px] py-3 px-5">
                  Item and price
                </th>
                <th className="text-left min-w-[200px] py-3 px-5">Status</th>
                <th className="text-left min-w-[250px] py-3 px-5">
                  Contact information
                </th>
                <th className="text-left min-w-[250px] py-3 px-5">
                  Confirmations
                </th>
                <th className="text-left min-w-[350px] py-3 px-5 rounded-tr-[12px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1E1E1E] divide-opacity-25">
              {txLoading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="py-8 text-center px-5 text-sm text-gray-500"
                  >
                    Loading transactions…
                  </td>
                </tr>
              ) : transactionsData?.transactions?.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="py-8 text-center px-5 text-sm text-gray-500"
                  >
                    No transactions found.
                  </td>
                </tr>
              ) : (
                transactionsData?.transactions?.map((transaction, idx) => (
                  <>
                    <tr className="my-1">
                      <td className="align-top py-3 px-5 text-[#000025] opacity-50 text-body ">
                        {transaction.item?.reference_code
                          ? transaction.item?.reference_code
                          : "N/A"}
                      </td>
                      <td className="align-top py-3 px-5">
                        <div className="flex flex-col gap-8">
                          <div className="flex items-center gap-4">
                            <div className="h-[50px] min-h-[50px] w-[50px] min-w-[50px] bg-gray-100 rounded overflow-hidden">
                              <img
                                src={transaction.item.images?.[0]?.file_url}
                                alt="thumb"
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <div>
                              <div className="font-medium text-[#1E1E1E] text-body line-clamp-1">
                                {transaction.item.title}
                              </div>
                              <div className="mt-1 text-[#000025] text-nav-d">
                                {transaction.item.category}
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="text-[#000025] text-[18px] font-[700]">
                              {transaction?.is_delivered_door && (
                                <div className="text-[#000025]  border border-[#000025] border-opacity-25 px-2.5 py-1 rounded-[8px] opacity-75 text-[12px] mb-2">
                                  Authentication check and doorstep delivery fee
                                  included.
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <div className="text-[#000025] opacity-50 text-[12px]">
                                  Buyer paid:
                                </div>{" "}
                                {transaction?.buyer_paid_amount} €
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="text-[#000025] opacity-50 text-[12px]">
                                  Seller payout:
                                </div>{" "}
                                {transaction?.seller_payout} €
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="align-top py-3 px-5">
                        <span
                          className={`text-[14px] font-[600] px-2.5 py-1 rounded-full capitalize whitespace-nowrap leading-none ${
                            transaction.status === "cancelled"
                              ? "bg-gray-200 text-gray-700"
                              : transaction.admin_released
                              ? "bg-[#B9FFCC] text-[#6C8F75]"
                              : transaction.buyer_confirmed &&
                                transaction.seller_confirmed
                              ? "bg-[#B9FFCC] text-[#16653F]"
                              : transaction.buyer_confirmed ||
                                transaction.seller_confirmed
                              ? "bg-[#FFC49F] text-[#D35100]"
                              : "bg-[#CBECF9] text-[#036]"
                          }`}
                        >
                          {transaction.status === "cancelled" ? (
                            <>
                              {transaction.payment_cancelled_reason ===
                              "item_not_authentication"
                                ? "Cancelled - Authentication Failed"
                                : "Cancelled"}
                            </>
                          ) : transaction.admin_released ? (
                            <>Funds received by the seller</>
                          ) : transaction.buyer_confirmed &&
                            transaction.seller_confirmed ? (
                            <>Both parties confirmed</>
                          ) : transaction.buyer_confirmed ? (
                            <>Buyer confirmed, awaiting seller confirmation</>
                          ) : transaction.seller_confirmed ? (
                            <>Seller confirmed, awaiting buyer confirmation</>
                          ) : (
                            <>verification in progress</>
                          )}
                        </span>
                      </td>
                      <td className="align-top py-3 px-5">
                        <div className="flex flex-col gap-4">
                          <div>
                            <div className="text-[14px] mb-2 font-[600] text-[#000025]">
                              Seller information
                            </div>
                            <div className="flex items-center gap-4">
                              <Avatar
                                imageUrl={transaction.seller.profile_image_url}
                                firstName={transaction.seller.firstName}
                                lastName={transaction.seller.lastName}
                              />
                              <div>
                                <div className="font-[600] text-nav-d text-[#1E1E1E] opacity-75">
                                  {transaction.seller.firstName}{" "}
                                  {transaction.seller.lastName}
                                </div>
                                <div className="text-[12px] opacity-50 text-[#1E1E1E]">
                                  {transaction.seller.email}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div className="text-[14px] mb-2 font-[600] text-[#000025]">
                              Buyer information
                            </div>
                            <div className="flex items-center gap-4">
                              <Avatar
                                imageUrl={transaction.buyer.profile_image_url}
                                firstName={transaction.buyer.firstName}
                                lastName={transaction.buyer.lastName}
                              />
                              <div>
                                <div className="font-[600] text-nav-d text-[#1E1E1E] opacity-75">
                                  {transaction.buyer.firstName}{" "}
                                  {transaction.buyer.lastName}
                                </div>
                                <div className="text-[12px] font-[500] opacity-50 text-[#1E1E1E]">
                                  {transaction.buyer.email}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="align-top py-3 px-5">
                        <div className="flex flex-col gap-4">
                          {transaction.status === "cancelled" ? (
                            <div className="flex items-center">
                              <button className="inline-flex cursor-default items-center gap-4 text-gray-600 text-subsection-title py-6 px-3 rounded-[8px]">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                >
                                  <path
                                    d="M13 1L1 13M1 1L13 13"
                                    stroke="#6B7280"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>{" "}
                                Transaction Cancelled
                              </button>
                            </div>
                          ) : (
                            <>
                              {transaction.seller_confirmed && (
                                <div className="flex items-center">
                                  <button className="inline-flex cursor-default items-center gap-4 text-[#16653F] text-subsection-title py-6 px-3 rounded-[8px]">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="13"
                                      viewBox="0 0 18 13"
                                      fill="none"
                                    >
                                      <path
                                        d="M17 1L6 12L1 7"
                                        stroke="#16653F"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>{" "}
                                    Confirmed by seller
                                  </button>
                                </div>
                              )}
                              {!transaction.seller_confirmed && (
                                <div className="flex items-center">
                                  <button className="inline-flex cursor-default items-center gap-4 text-[#D35100] text-subsection-title py-6 px-3 rounded-[8px]">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      fill="none"
                                    >
                                      <path
                                        d="M13 1L1 13M1 1L13 13"
                                        stroke="#D35100"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>{" "}
                                    Awaiting confirmation
                                  </button>
                                </div>
                              )}
                              {transaction.buyer_confirmed && (
                                <div className="flex items-center">
                                  <button className="inline-flex cursor-default items-center gap-4 text-[#16653F] text-subsection-title py-6 px-3 rounded-[8px]">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="13"
                                      viewBox="0 0 18 13"
                                      fill="none"
                                    >
                                      <path
                                        d="M17 1L6 12L1 7"
                                        stroke="#16653F"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>{" "}
                                    Confirmed by buyer
                                  </button>
                                </div>
                              )}
                              {!transaction.buyer_confirmed && (
                                <div className="flex items-center">
                                  <button className="inline-flex cursor-default items-center gap-4 text-[#D35100] text-subsection-title py-6 px-3 rounded-[8px]">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      fill="none"
                                    >
                                      <path
                                        d="M13 1L1 13M1 1L13 13"
                                        stroke="#D35100"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>{" "}
                                    Awaiting confirmation
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="align-top py-3 px-5">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => handleOpenPreview(transaction)}
                            className="opacity-50 border border-[#1E1E1E] rounded-[8px] flex items-center justify-center border-opacity-50 min-h-[42px] min-w-[42px]"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M2.42012 12.7132C2.28394 12.4975 2.21584 12.3897 2.17772 12.2234C2.14909 12.0985 2.14909 11.9015 2.17772 11.7766C2.21584 11.6103 2.28394 11.5025 2.42012 11.2868C3.54553 9.50484 6.8954 5 12.0004 5C17.1054 5 20.4553 9.50484 21.5807 11.2868C21.7169 11.5025 21.785 11.6103 21.8231 11.7766C21.8517 11.9015 21.8517 12.0985 21.8231 12.2234C21.785 12.3897 21.7169 12.4975 21.5807 12.7132C20.4553 14.4952 17.1054 19 12.0004 19C6.8954 19 3.54553 14.4952 2.42012 12.7132Z"
                                stroke="#1E1E1E"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12.0004 15C13.6573 15 15.0004 13.6569 15.0004 12C15.0004 10.3431 13.6573 9 12.0004 9C10.3435 9 9.0004 10.3431 9.0004 12C9.0004 13.6569 10.3435 15 12.0004 15Z"
                                stroke="#1E1E1E"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          <div className="flex items-center gap-4 flex-wrap">
                            {/* Accept Payment button - shows only when status is pending */}
                            {transaction.status === "pending" &&
                              transaction.status !== "cancelled" && (
                                <button
                                  onClick={() =>
                                    handleAcceptPayment(transaction)
                                  }
                                  disabled={acceptingPayment === transaction.id}
                                  className="inline-flex items-center gap-4 bg-blue-gradient text-white text-subsection-title py-2.5 px-3 rounded-[8px] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#0d4a2a] transition-colors"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="13"
                                    viewBox="0 0 18 13"
                                    fill="none"
                                  >
                                    <path
                                      d="M17 1L6 12L1 7"
                                      stroke="white"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  {acceptingPayment === transaction.id
                                    ? "Accepting..."
                                    : "Confirm Payment Transfer"}
                                </button>
                              )}
                            {!(
                              transaction.buyer_confirmed &&
                              transaction.seller_confirmed
                            ) &&
                              transaction.status !== "cancelled" && (
                                <button
                                  onClick={() =>
                                    handleSendReminder(transaction)
                                  }
                                  disabled={sendingReminder === transaction.id}
                                  className={`inline-flex items-center gap-4 text-subsection-title py-2.5 px-3 rounded-[8px] disabled:opacity-50 disabled:cursor-not-allowed ${
                                    sentReminder === transaction.id
                                      ? "bg-[#DCFCE7] text-[#16653F] border border-[#A0E8B8]"
                                      : "bg-[#F5F8FF] text-[#2A8BF7]"
                                  }`}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M21.5 18L14.8571 12M9.14286 12L2.50003 18M2 7L10.1649 12.7154C10.8261 13.1783 11.1567 13.4097 11.5163 13.4993C11.8339 13.5785 12.1661 13.5785 12.4837 13.4993C12.8433 13.4097 13.1739 13.1783 13.8351 12.7154L22 7M6.8 20H17.2C18.8802 20 19.7202 20 20.362 19.673C20.9265 19.3854 21.3854 18.9265 21.673 18.362C22 17.7202 22 16.8802 22 15.2V8.8C22 7.11984 22 6.27976 21.673 5.63803C21.3854 5.07354 20.9265 4.6146 20.362 4.32698C19.7202 4 18.8802 4 17.2 4H6.8C5.11984 4 4.27976 4 3.63803 4.32698C3.07354 4.6146 2.6146 5.07354 2.32698 5.63803C2 6.27976 2 7.11984 2 8.8V15.2C2 16.8802 2 17.7202 2.32698 18.362C2.6146 18.9265 3.07354 19.3854 3.63803 19.673C4.27976 20 5.11984 20 6.8 20Z"
                                      stroke={
                                        sentReminder === transaction.id
                                          ? "#16653F"
                                          : "#2A8BF7"
                                      }
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  {sendingReminder === transaction.id
                                    ? "Sending..."
                                    : sentReminder === transaction.id
                                    ? "Email sent"
                                    : "Send reminder email"}
                                </button>
                              )}
                            {["pending", "accepted"].includes(
                              transaction.status
                            ) &&
                              transaction.status !== "cancelled" && (
                                <button
                                  onClick={() =>
                                    handleReportMissingPayment(transaction)
                                  }
                                  disabled={
                                    reportingMissingPayment === transaction.id
                                  }
                                  className={`inline-flex items-center gap-4 text-subsection-title py-2.5 px-3 rounded-[8px] disabled:opacity-50 disabled:cursor-not-allowed ${
                                    reportedMissingPayment === transaction.id
                                      ? "bg-[#FDECEC] text-[#B42318]"
                                      : "bg-[#FFF5E6] text-[#B45309]"
                                  }`}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M12 9V13"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M12 17H12.01"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <path
                                      d="M10.29 3.86002L1.82 18C1.6453 18.3026 1.55272 18.6498 1.552 19.0035C1.55129 19.3571 1.64246 19.7046 1.816 20.008C1.98954 20.3114 2.23932 20.5592 2.53726 20.7255C2.83521 20.8918 3.1703 20.9698 3.51 20.95H20.49C20.8297 20.9698 21.1648 20.8918 21.4627 20.7255C21.7607 20.5592 22.0105 20.3114 22.184 20.008C22.3575 19.7046 22.4487 19.3571 22.448 19.0035C22.4473 18.6498 22.3547 18.3026 22.18 18L13.71 3.86002C13.5296 3.56599 13.2814 3.32242 12.9883 3.15268C12.6951 2.98293 12.3653 2.89209 12.03 2.89002C11.6946 2.88794 11.3637 2.9747 11.0687 3.14176C10.7738 3.30883 10.5244 3.55148 10.34 3.84502L10.29 3.86002Z"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  {reportingMissingPayment === transaction.id
                                    ? "Sending..."
                                    : reportedMissingPayment === transaction.id
                                    ? "Notified"
                                    : "Payment not received"}
                                </button>
                              )}
                            {/* Reject Product - Authentication Failed button - shows only when is_delivered_door is true and status is not cancelled/released */}
                            {transaction.is_delivered_door &&
                              !["cancelled", "released"].includes(
                                transaction.status
                              ) && (
                                <button
                                  onClick={() =>
                                    handleOpenCancelAuthModal(transaction)
                                  }
                                  disabled={
                                    cancellingPayment === transaction.id ||
                                    cancelledPayment === transaction.id
                                  }
                                  className={`inline-flex items-center justify-center gap-2 text-subsection-title py-2.5 px-3 rounded-[8px] disabled:opacity-50 disabled:cursor-not-allowed ${
                                    cancelledPayment === transaction.id
                                      ? "bg-[#FDECEC] text-[#B42318] border border-[#FCA5A5]"
                                      : "bg-[#FEE2E2] text-[#DC2626] hover:bg-[#FECACA] transition-colors"
                                  }`}
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                    className="flex-shrink-0"
                                  >
                                    <path
                                      d="M13 1L1 13M1 1L13 13"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  <span>
                                    {cancellingPayment === transaction.id
                                      ? "Cancelling..."
                                      : cancelledPayment === transaction.id
                                      ? "Cancelled"
                                      : "Authentication Check Failed"}
                                  </span>
                                </button>
                              )}
                            {transaction.buyer_confirmed &&
                              transaction.seller_confirmed &&
                              !transaction.admin_released && (
                                <button
                                  onClick={() =>
                                    handleReleaseFunds(transaction)
                                  }
                                  disabled={releasingFunds === transaction.id}
                                  className="inline-flex items-center gap-4 bg-blue-gradient text-white text-subsection-title py-2.5 px-4 rounded-[8px] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="22"
                                    viewBox="0 0 14 22"
                                    fill="none"
                                  >
                                    <path
                                      d="M1 15C1 17.2091 2.79086 19 5 19H9C11.2091 19 13 17.2091 13 15C13 12.7909 11.2091 11 9 11H5C2.79086 11 1 9.20914 1 7C1 4.79086 2.79086 3 5 3H9C11.2091 3 13 4.79086 13 7M7 1V21"
                                      stroke="white"
                                      stroke-width="2"
                                      stroke-linecap="round"
                                      stroke-linejoin="round"
                                    />
                                  </svg>
                                  {releasingFunds === transaction.id
                                    ? "Releasing..."
                                    : "Release funds"}
                                </button>
                              )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <TransactionPreviewModal
        open={previewOpen}
        onClose={handleClosePreview}
        transactionId={selectedTransactionId}
      />
      <CancelPaymentAuthenticationFailedModal
        isOpen={cancelAuthModalOpen}
        onClose={handleCloseCancelAuthModal}
        onConfirm={handleCancelPaymentAuthenticationFailed}
        isLoading={cancellingPayment === selectedTransactionForCancel?.id}
        errorText={txError}
      />
    </LayoutAdmin>
  );
};

export default AdminTransactions;
